import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Star, Users, Clock, Code, Zap, Shield, TrendingUp, Sparkles, Rocket, Lock, Calendar } from 'lucide-react';

const HomePage: React.FC = () => {
  const trustIndicators = [
    { icon: Clock, text: 'Fast Delivery' },
    { icon: Code, text: 'Complete Source Code' },
    { icon: Shield, text: 'Bank-Level Security' }
  ];

  const features = [
    {
      icon: Users,
      title: 'Complete Client Management',
      description: 'Advanced CRM with client profiles, service history, and communication tracking'
    },
    {
      icon: TrendingUp,
      title: 'Revenue Analytics',
      description: 'Real-time financial dashboards with comprehensive reporting and insights'
    },
    {
      icon: Zap,
      title: 'Automated Booking System',
      description: 'Smart scheduling with automated confirmations and calendar management'
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Bank-level security with audit logs and compliance reporting'
    }
  ];

  const businessModelFlexibility = [
    {
      model: 'SaaS Subscription',
      description: 'Customize and offer monthly/yearly subscriptions under your brand',
      potential: '$50-500/month per client'
    },
    {
      model: 'One-Time License',
      description: 'Rebrand and provide one-time solutions to businesses',
      potential: '$5,000-50,000 per license'
    },
    {
      model: 'Custom Enterprise',
      description: 'Transform into custom enterprise solutions for large organizations',
      potential: '$25,000-250,000 per project'
    },
    {
      model: 'Niche-Specific Platform',
      description: 'Fully customize for specific industries and launch as your own branded SaaS',
      potential: 'Unlimited scalability'
    }
  ];

  const successStories = [
    {
      name: 'ClinicIT',
      business: 'Medical Practice Management',
      result: 'Live Production System',
      quote: 'We took the white-label platform, customized it for medical clinics, and launched it as ClinicIT - our own branded solution. Now we serve real medical practices with a unified booking system under our brand name.',
      rating: 5,
      isLiveExample: true,
      url: 'https://clinicit.live'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-innovation text-white relative overflow-hidden">
      {/* Global Animated Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-transparent"></div>
      <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
      <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-purple-500/5 rounded-full blur-2xl animate-pulse" style={{animationDelay: '4s'}}></div>
      <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-blue-500/8 rounded-full blur-3xl animate-pulse" style={{animationDelay: '6s'}}></div>
      
      {/* Hero Section */}
      <section className="py-12 py-20 relative z-10 h-screen flex items-center">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 w-full">
          <div className="text-center space-y-5">
            {/* Main Heading */}
            <div className="space-y-4">
              <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20 mb-3">
                <Zap className="h-4 w-4 text-cyan-400 mr-2" />
                <span className="text-sm font-medium text-slate-200">Revolutionary White-Label Platform</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold leading-tight max-w-4xl mx-auto">
                <span className="block mb-2">Launch Your Own</span>
                <span className="block bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Business Platform
                </span>
              </h1>
              
              <p className="text-lg md:text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
                Get a <span className="font-bold text-cyan-300">$50K+ enterprise platform</span> for only $799. 
                Customize it, brand it as <span className="font-bold text-yellow-300">YOUR own</span>, and launch your business.
              </p>
            </div>
            
            {/* Trust Indicators */}
            {/* Trust Indicators */}
            <div className="flex justify-center gap-4 max-w-2xl mx-auto flex-wrap">
              {trustIndicators.map((item, index) => (
                <div key={index} className="flex flex-col items-center space-y-2 bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:border-cyan-400/30 transition-all duration-300 group">
                  <div className="bg-gradient-to-br from-cyan-400/20 to-blue-400/20 rounded-full p-2 group-hover:scale-110 transition-transform duration-300">
                    <item.icon className="h-5 w-5 text-cyan-400" />
                  </div>
                  <span className="font-medium text-white text-center text-xs">{item.text}</span>
                </div>
              ))}
            </div>

            {/* Call to Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-xl mx-auto">
              <Link
                to="/pricing"
                className="group relative bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-3 rounded-xl text-base font-bold hover:from-cyan-400 hover:to-blue-400 transition-all duration-300 flex items-center justify-center shadow-2xl hover:shadow-cyan-500/25 transform hover:scale-105 min-w-[180px]"
              >
                <span className="mr-2">Get Instant Access</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              
              <Link
                to="/demo"
                className="group border-2 border-cyan-400/50 text-cyan-400 px-8 py-3 rounded-xl text-base font-bold hover:bg-cyan-400/10 hover:border-cyan-400 transition-all duration-300 backdrop-blur-sm min-w-[180px] hover:scale-105 transform"
              >
                <span className="mr-2">Watch Demo</span>
                <span className="text-lg group-hover:scale-110 transition-transform duration-300">▶</span>
              </Link>
            </div>
            
            {/* Additional Info */}
            <div className="text-center space-y-1 max-w-xl mx-auto">
              <p className="text-slate-200 text-sm">
                <Sparkles className="h-4 w-4 text-yellow-400 inline mr-1" />
                <span className="font-semibold">Smart entrepreneurs</span> already own their platform
              </p>
              <p className="text-slate-400 text-xs flex items-center justify-center space-x-3">
                <span className="flex items-center space-x-1">
                  <Rocket className="h-3 w-3" />
                  <span>Fast deployment</span>
                </span>
                <span>•</span>
                <span className="flex items-center space-x-1">
                  <Zap className="h-3 w-3" />
                  <span>No fees</span>
                </span>
                <span>•</span>
                <span className="flex items-center space-x-1">
                  <Lock className="h-3 w-3" />
                  <span>Full ownership</span>
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Two Offer Cards */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Choose Your White-Label Package
            </h2>
            <p className="text-xl text-slate-200 max-w-3xl mx-auto">
              Get the platform → Customize it → Launch under <span className="text-yellow-300 font-semibold">YOUR brand</span>
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* DIY Package */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl shadow-xl border border-white/10 p-8 relative hover:shadow-2xl hover:border-cyan-400/30 transition-all duration-300 group">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">DIY White-Label Package</h3>
                <div className="text-4xl font-bold text-cyan-400 mb-2">$799</div>
                <p className="text-slate-200">One-time payment</p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-200">Complete source code and system</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-200">Full white-label rights</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-200">Full source code access</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-200">Setup guide and tutorials</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-200">30 days email support</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-200">All features and modules</span>
                </li>
              </ul>

              <p className="text-sm text-slate-200 mb-6 italic">
                Perfect for developers and tech-savvy entrepreneurs
              </p>

              <Link
                to="/pricing"
                className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-cyan-500 hover:to-blue-500 transition-all duration-200 font-semibold text-center block shadow-lg hover:shadow-cyan-500/25 transform hover:scale-105"
              >
                Choose DIY Package
              </Link>
            </div>

            {/* Done-For-You Package */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl shadow-xl border-2 border-cyan-400/20 p-8 relative hover:shadow-2xl hover:border-cyan-400/40 transition-all duration-300 group">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                  MOST POPULAR
                </span>
              </div>

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">Done-For-You White-Label</h3>
                <div className="text-4xl font-bold text-cyan-400 mb-2">$1,299</div>
                <p className="text-slate-200">One-time payment</p>
                <p className="text-yellow-300 font-semibold text-sm mt-2">We customize it for you!</p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-200">Everything in DIY Package</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-200"><strong className="text-cyan-400">We customize for your niche</strong></span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-200">Custom branding and design</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-200">Industry-specific features</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-200">Fast-track service</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-200">90 days premium support</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-200">Training session included</span>
                </li>
              </ul>

              <p className="text-sm text-slate-200 mb-6 italic">
                Perfect for non-technical business owners
              </p>

              <Link
                to="/pricing"
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-lg hover:from-cyan-400 hover:to-blue-400 transition-all duration-200 font-semibold text-center block shadow-lg hover:shadow-cyan-500/25 transform hover:scale-105"
              >
                Choose Done-For-You
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Highlights */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Enterprise-Grade Features
            </h2>
            <p className="text-xl text-slate-200">
              Everything you need to run a professional business management platform
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center p-6 rounded-xl hover:bg-slate-700/30 transition-all duration-200 group">
                <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-200">
                  <feature.icon className="h-8 w-8 text-cyan-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-200">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/features"
              className="inline-flex items-center text-cyan-400 hover:text-cyan-300 font-semibold text-lg transition-colors duration-200 group"
            >
              View All Features
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
          </div>
        </div>
      </section>

      {/* Business Model Flexibility */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Unlimited Business Model Flexibility
            </h2>
            <p className="text-xl text-slate-300 max-w-4xl mx-auto">
              Get this ready business-in-a-box for just <span className="text-yellow-300 font-bold">$799</span> or <span className="text-yellow-300 font-bold">$1,299</span> and 
              sell it using multiple pricing models simultaneously. <span className="text-cyan-300 font-semibold">Your investment pays for itself with just one client!</span>
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {businessModelFlexibility.map((model, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:shadow-lg transition-shadow duration-200">
                <h3 className="text-xl font-bold text-white mb-2">{model.model}</h3>
                <p className="text-slate-300 mb-4">{model.description}</p>
                <div className="bg-cyan-100 p-3 rounded-lg">
                  <span className="text-cyan-900 font-semibold">Revenue Potential: </span>
                  <span className="text-slate-900 font-bold">{model.potential}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-green-500/20 to-blue-500/20 backdrop-blur-sm border border-green-300/30 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">
              Real Success Example
            </h3>
            <p className="text-lg text-slate-200 mb-4">
              One of our partners started with 5 clients at $200/month each. Within 18 months, 
              they scaled to 150+ clients and now generate over $40,000/month in recurring revenue.
            </p>
            <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full font-semibold">
              <TrendingUp className="mr-2 h-5 w-5" />
              $40K+ Monthly Recurring Revenue
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories Carousel */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Partner Success Stories
            </h2>
            <p className="text-xl text-slate-200">
              See how our white-label partners are transforming their businesses
            </p>
          </div>

          <div className="flex justify-center">
            {successStories.map((story, index) => (
              <div key={index} className="bg-slate-800/80 backdrop-blur-sm rounded-xl shadow-xl p-8 border border-slate-700/50 hover:border-cyan-500/50 transition-all duration-300 relative max-w-2xl w-full">
                {story.isLiveExample && (
                  <div className="absolute -top-3 -right-3">
                    <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                      LIVE EXAMPLE
                    </div>
                  </div>
                )}
                <div className="flex items-center mb-4">
                  {[...Array(story.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <blockquote className="text-slate-200 mb-4 italic">
                  "{story.quote}"
                </blockquote>
                <div className="border-t border-slate-700/50 pt-4">
                  <div className="font-semibold text-white">{story.name}</div>
                  <div className="text-sm text-slate-400">{story.business}</div>
                  <div className="text-sm font-semibold text-cyan-400 mt-1">{story.result}</div>
                  {story.url && (
                    <a 
                      href={story.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center mt-3 text-yellow-400 hover:text-yellow-300 font-semibold text-sm transition-colors duration-200 group"
                    >
                      Visit Live Site
                      <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/success-stories"
              className="inline-flex items-center text-cyan-400 hover:text-cyan-300 font-semibold text-lg transition-colors duration-200 group"
            >
              Read More Success Stories
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
          </div>
        </div>
      </section>

      {/* Live ClinicIT Demo */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-green-400 font-bold text-lg">LIVE PRODUCTION SYSTEM</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              See ClinicIT in Action
            </h2>
            <p className="text-xl text-slate-300 max-w-4xl mx-auto mb-8">
              This is a real medical practice management platform built with our white-label system. 
              <span className="text-cyan-300 font-semibold"> Interact with it directly</span> - it's serving actual medical clinics right now.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-cyan-400/30 transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-3 rounded-xl">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">ClinicIT - Medical Practice Management</h3>
                  <p className="text-slate-300">HIPAA-compliant system for unified booking and patient management</p>
                </div>
              </div>
              <a 
                href="https://clinicit.live" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2"
              >
                Open Full Site
                <ArrowRight className="h-5 w-5" />
              </a>
            </div>

            <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
              <div className="flex items-center gap-2 mb-4">
                <div className="flex gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div className="bg-slate-800 rounded px-3 py-1 text-slate-300 text-sm font-mono">
                  https://clinicit.live
                </div>
              </div>
              
              <div className="relative">
                <iframe
                  src="https://clinicit.live"
                  className="w-full h-[600px] rounded-lg border border-slate-600/50"
                  title="ClinicIT Live Demo"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-transparent via-transparent to-slate-900/10 pointer-events-none rounded-lg"></div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center">
                <div className="bg-blue-500/20 p-3 rounded-lg w-fit mx-auto mb-4">
                  <Calendar className="h-8 w-8 text-blue-400" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">Unified Booking</h4>
                <p className="text-slate-300 text-sm">Online appointments + walk-in patients in one system</p>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center">
                <div className="bg-green-500/20 p-3 rounded-lg w-fit mx-auto mb-4">
                  <Shield className="h-8 w-8 text-green-400" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">HIPAA Compliant</h4>
                <p className="text-slate-300 text-sm">Medical-grade security and compliance features</p>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center">
                <div className="bg-purple-500/20 p-3 rounded-lg w-fit mx-auto mb-4">
                  <Zap className="h-8 w-8 text-purple-400" />
                </div>
                <h4 className="text-lg font-bold text-white mb-2">Live Production</h4>
                <p className="text-slate-300 text-sm">Real system serving actual medical practices</p>
              </div>
            </div>

            <div className="mt-8 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-400/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Sparkles className="h-6 w-6 text-cyan-400" />
                <h4 className="text-xl font-bold text-white">Built from Our $1,299 Platform</h4>
              </div>
              <p className="text-slate-300 mb-4">
                They didn't resell our platform - they transformed it into <strong className="text-white">ClinicIT, their own branded SaaS</strong>. 
                You can do the same: customize, rebrand, and launch as <strong className="text-yellow-300">YOUR company's solution</strong>.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="bg-cyan-400/20 text-cyan-300 px-3 py-1 rounded-full text-sm font-medium">Custom Branding</span>
                <span className="bg-cyan-400/20 text-cyan-300 px-3 py-1 rounded-full text-sm font-medium">Industry-Specific Features</span>
                <span className="bg-cyan-400/20 text-cyan-300 px-3 py-1 rounded-full text-sm font-medium">Professional UI/UX</span>
                <span className="bg-cyan-400/20 text-cyan-300 px-3 py-1 rounded-full text-sm font-medium">Full Source Code</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 relative z-10 text-white">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 to-transparent"></div>
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Launch Your Own Platform?
          </h2>
          <p className="text-xl mb-8 text-slate-200">
            Join smart entrepreneurs who own their business management platform
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/pricing"
              className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-4 rounded-lg text-lg font-bold hover:from-cyan-400 hover:to-blue-400 transition-all duration-200 shadow-lg hover:shadow-cyan-500/25 transform hover:scale-105"
            >
              Get Started Today
            </Link>
            <Link
              to="/demo"
              className="border-2 border-cyan-400 text-cyan-400 px-8 py-4 rounded-lg text-lg font-bold hover:bg-cyan-400 hover:text-slate-900 transition-all duration-200 backdrop-blur-sm"
            >
              Schedule Demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
